import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'
import { Plus } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import {
  apiDownloadSuppliersCsv,
  apiListSuppliers,
  isApiError,
  type ListSuppliersParams,
  type SupplierListFilter,
  type SupplierListResponse,
  type SupplierSortBy,
  type SupplierSortOrder,
} from '@/lib/api'
import { triggerBlobDownload } from '@/lib/download'
import { Button } from '../ui/button'
import { Skeleton } from '../ui/skeleton'
import { EmptyState } from '../EmptyState'
import { SupplierFilters } from './SupplierFilters'
import { SupplierSearchInput } from './SupplierSearchInput'
import { SuppliersTable } from './SuppliersTable'
import { CreateSupplierDialog } from './CreateSupplierDialog'
import { SupplierProfile } from './SupplierProfile'
import { BulkActions } from './BulkActions'
import { BulkRemindModal } from './BulkRemindModal'

const DEFAULT_PAGE_SIZE = 50
const MAX_INITIAL_PAGE = 10_000

// UUID v1-v5 — suffisant pour valider l'input avant de l'envoyer au back.
// Le back re-valide ensuite via Zod (422 si malformé), la validation client
// évite juste un aller-retour inutile.
const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

function parseInitialState(): {
  filter: SupplierListFilter
  search: string
  sortBy: SupplierSortBy
  sortOrder: SupplierSortOrder
  page: number
  activeSupplierId: string | null
} {
  const params = new URLSearchParams(window.location.search)
  const statusRaw = params.get('status') as SupplierListFilter | null
  const allowedStatuses: SupplierListFilter[] = ['all', 'pending', 'received', 'validated', 'overdue']
  const filter: SupplierListFilter =
    statusRaw && allowedStatuses.includes(statusRaw) ? statusRaw : 'all'
  const sortByRaw = params.get('sortBy') as SupplierSortBy | null
  const allowedSort: SupplierSortBy[] = ['name', 'createdAt', 'updatedAt', 'status', 'lastActivity']
  const sortBy: SupplierSortBy = sortByRaw && allowedSort.includes(sortByRaw) ? sortByRaw : 'createdAt'
  const sortOrderRaw = params.get('sortOrder')
  const sortOrder: SupplierSortOrder = sortOrderRaw === 'asc' ? 'asc' : 'desc'
  const pageRaw = Number(params.get('page'))
  const page =
    Number.isInteger(pageRaw) && pageRaw > 0 ? Math.min(pageRaw, MAX_INITIAL_PAGE) : 1
  const search = params.get('search') ?? ''
  const view = params.get('view')
  const supplierIdRaw = params.get('supplierId')
  const activeSupplierId =
    view === 'profile' && supplierIdRaw && UUID_RE.test(supplierIdRaw) ? supplierIdRaw : null
  return { filter, search, sortBy, sortOrder, page, activeSupplierId }
}

function syncUrl(state: {
  filter: SupplierListFilter
  search: string
  sortBy: SupplierSortBy
  sortOrder: SupplierSortOrder
  page: number
  activeSupplierId: string | null
}) {
  const params = new URLSearchParams()
  if (state.activeSupplierId) {
    params.set('view', 'profile')
    params.set('supplierId', state.activeSupplierId)
  }
  // Les params de liste sont préservés même en vue profil pour restaurer
  // le state exactement lors du retour (pagination, filtres, tri).
  if (state.filter !== 'all') params.set('status', state.filter)
  if (state.search) params.set('search', state.search)
  if (state.sortBy !== 'createdAt') params.set('sortBy', state.sortBy)
  if (state.sortOrder !== 'desc') params.set('sortOrder', state.sortOrder)
  if (state.page !== 1) params.set('page', String(state.page))
  const query = params.toString()
  const newUrl = `${window.location.pathname}${query ? `?${query}` : ''}`
  window.history.replaceState(null, '', newUrl)
}

type SuppliersProps = {
  // P4 + Story 3.6 : le drill-in « nom de campagne » depuis le profil
  // fournisseur remonte vers App qui pilote `activeView`. Les options
  // permettent d'ouvrir directement la vue campagne × fournisseur et de
  // mémoriser l'origine pour le retour contextuel. Optionnel pour rester
  // testable en isolation.
  onNavigateToCampaign?: (
    campaignId: string,
    options?: { campaignSupplierId?: string; originSupplierId?: string }
  ) => void
}

export function Suppliers({ onNavigateToCampaign }: SuppliersProps = {}) {
  const { logout, user } = useAuth()
  const initial = useMemo(parseInitialState, [])

  const [filter, setFilter] = useState<SupplierListFilter>(initial.filter)
  const [search, setSearch] = useState<string>(initial.search)
  const [sortBy, setSortBy] = useState<SupplierSortBy>(initial.sortBy)
  const [sortOrder, setSortOrder] = useState<SupplierSortOrder>(initial.sortOrder)
  const [page, setPage] = useState<number>(initial.page)
  const [activeSupplierId, setActiveSupplierId] = useState<string | null>(
    initial.activeSupplierId
  )

  const [data, setData] = useState<SupplierListResponse | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isReloading, setIsReloading] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [createOpen, setCreateOpen] = useState(false)

  // Story 2.4 — sélection bulk. `selectedIds` peut contenir des UUIDs qui ne
  // sont plus dans `data.data` entre deux renders (ex. pendant un reload) ;
  // l'effet ci-dessous garantit l'invariant `selectedIds ⊆ page courante`
  // en vidant à chaque changement de filtre/recherche/tri/page.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set())
  const [isExporting, setIsExporting] = useState(false)
  const [remindOpen, setRemindOpen] = useState(false)
  const bulkResetToastShownRef = useRef(false)

  const searchInputRef = useRef<HTMLInputElement | null>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)

  const firstLoadRef = useRef(true)
  // P6 : compteur monotone pour ignorer les réponses des requêtes obsolètes
  // (une recherche rapide peut générer plusieurs requêtes ; seule la plus
  // récente doit être appliquée).
  const requestSeqRef = useRef(0)
  // P8 : on ne refait un fetch liste que si les filtres de liste changent.
  // Le retour depuis la vue profil ne doit pas relancer la requête : le data
  // est conservé tel quel pour un retour instantané sans flash.
  const prevActiveSupplierIdRef = useRef<string | null>(initial.activeSupplierId)

  const loadData = useCallback(async () => {
    const params: ListSuppliersParams = {
      page,
      pageSize: DEFAULT_PAGE_SIZE,
      search: search || undefined,
      status: filter,
      sortBy,
      sortOrder,
    }
    if (firstLoadRef.current) {
      setIsLoading(true)
    } else {
      setIsReloading(true)
    }
    const seq = ++requestSeqRef.current
    try {
      const res = await apiListSuppliers(params)
      if (seq !== requestSeqRef.current) return
      setData(res)
      setLoadError(null)
    } catch (err) {
      if (seq !== requestSeqRef.current) return
      const msg = err instanceof Error ? err.message : 'Erreur lors de la récupération des fournisseurs'
      if (/^API error 401\b/.test(msg) || /^Non authentifié/i.test(msg)) {
        logout()
      }
      setLoadError(msg)
      toast.error(msg)
    } finally {
      if (seq === requestSeqRef.current) {
        firstLoadRef.current = false
        setIsLoading(false)
        setIsReloading(false)
      }
    }
  }, [page, search, filter, sortBy, sortOrder, logout])

  useEffect(() => {
    // P8 : recharger la liste uniquement si :
    //   - on n'est pas en vue profil, ET
    //   - on ne vient PAS de fermer la vue profil (prev != null → null).
    // Le retour depuis le profil doit restituer la liste telle quelle, sans
    // re-fetch qui provoquerait un flash de skeleton/reloading.
    const wasProfile = prevActiveSupplierIdRef.current !== null
    if (activeSupplierId === null && !wasProfile) {
      loadData()
    }
    prevActiveSupplierIdRef.current = activeSupplierId
    syncUrl({ filter, search, sortBy, sortOrder, page, activeSupplierId })
  }, [loadData, filter, search, sortBy, sortOrder, page, activeSupplierId])

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key !== '/') return
      const target = e.target as HTMLElement | null
      const tag = target?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA') return
      e.preventDefault()
      searchInputRef.current?.focus()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  // Reset sélection au changement de filtre/recherche/tri/page.
  // Snapshot via ref pour éviter la boucle infinie (selectedIds dans le dep
  // array → setSelectedIds → re-render → effet re-run). Le ref capture la
  // valeur courante sans déclencher de re-render.
  const selectedIdsSnapshot = useRef(selectedIds)
  selectedIdsSnapshot.current = selectedIds
  useEffect(() => {
    if (selectedIdsSnapshot.current.size > 0) {
      setSelectedIds(new Set())
      if (!bulkResetToastShownRef.current) {
        toast.info('Sélection réinitialisée', { id: 'bulk-reset' })
        bulkResetToastShownRef.current = true
      }
    }
  }, [filter, search, sortBy, sortOrder, page])

  const handleFilterChange = useCallback((next: SupplierListFilter) => {
    setFilter(next)
    setPage(1)
  }, [])

  const handleSearch = useCallback((value: string) => {
    setSearch(value)
    setPage(1)
  }, [])

  const handleSort = useCallback(
    (column: SupplierSortBy) => {
      if (column === sortBy) {
        setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'))
      } else {
        setSortBy(column)
        setSortOrder('asc')
      }
      setPage(1)
    },
    [sortBy]
  )

  const totalPages = data ? Math.max(1, Math.ceil(data.total / DEFAULT_PAGE_SIZE)) : 1
  const showInitialSkeleton = isLoading && data === null

  const handleOpenProfile = useCallback((id: string) => {
    setActiveSupplierId(id)
  }, [])

  const handleCloseProfile = useCallback(() => {
    setActiveSupplierId(null)
  }, [])

  const handleToggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const handleToggleSelectAll = useCallback(() => {
    if (!data) return
    setSelectedIds((prev) => {
      const allSelected =
        data.data.length > 0 && data.data.every((s) => prev.has(s.id))
      const next = new Set(prev)
      if (allSelected) {
        // Retirer uniquement les IDs de la page courante (scope strict),
        // jamais d'autres IDs — même si l'effet reset cross-page les vide
        // aussi en pratique, on ne veut pas dépendre de cet invariant ici.
        data.data.forEach((s) => next.delete(s.id))
        return next
      }
      data.data.forEach((s) => next.add(s.id))
      return next
    })
  }, [data])

  const handleClearSelection = useCallback(() => {
    setSelectedIds(new Set())
  }, [])

  // Story 3.2 — snapshot des noms des fournisseurs sélectionnés pour affichage
  // dans la modale sans re-fetch. Borné à la page courante : si un ID
  // sélectionné n'est plus visible (changement de page/filtre), il disparaît
  // de la liste affichée, mais l'ID reste bien envoyé au backend.
  const selectedSupplierNames = useMemo(() => {
    if (!data) return []
    return data.data.filter((s) => selectedIds.has(s.id)).map((s) => s.name)
  }, [data, selectedIds])

  const handleOpenRemind = useCallback(() => {
    if (selectedIds.size === 0) return
    setRemindOpen(true)
  }, [selectedIds])

  const handleRemindSuccess = useCallback(
    (result: { sent: number; failed: number }) => {
      // Patch P-5 (code review Story 3.2) : ne clearer la sélection que si
      // des relances sont effectivement parties. Si `sent === 0 && failed === 0`
      // (uniquement des `skipped`), Marie doit pouvoir ajuster la sélection.
      if (result.failed === 0 && result.sent > 0) {
        setSelectedIds(new Set())
      }
      loadData()
    },
    [loadData]
  )

  const handleExport = useCallback(async () => {
    if (selectedIds.size === 0) return
    setIsExporting(true)
    try {
      const ids = Array.from(selectedIds)
      const { blob, filename, exportCount } = await apiDownloadSuppliersCsv(ids)
      triggerBlobDownload(blob, filename)
      if (exportCount === 0) {
        toast.warning('Aucun fournisseur à exporter')
      } else {
        toast.success(
          `${exportCount} fournisseur${exportCount > 1 ? 's' : ''} exporté${exportCount > 1 ? 's' : ''}`
        )
        // Alerte partielle : certains IDs n'ont pas été exportés (filtrés
        // cross-tenant côté serveur ou supprimés entre la sélection et le
        // clic). Sans ce toast, l'utilisateur pense que tout est parti.
        if (exportCount < ids.length) {
          const missing = ids.length - exportCount
          toast.warning(
            `${missing} fournisseur${missing > 1 ? 's' : ''} non exporté${missing > 1 ? 's' : ''} (introuvable${missing > 1 ? 's' : ''})`
          )
        }
      }
    } catch (err) {
      // 401 détecté via `ApiError.status` — pas via le message, qui est la
      // valeur du champ `error` du JSON (ex. "Non authentifié") sans prefix.
      if (isApiError(err) && err.status === 401) {
        logout()
        return
      }
      const msg = err instanceof Error ? err.message : 'Erreur lors de l\'export'
      toast.error(msg)
    } finally {
      setIsExporting(false)
    }
  }, [selectedIds, logout])

  if (activeSupplierId) {
    // Story 3.6 — on enrichit le clic « ouvrir campagne » du contexte
    // d'origine (`originSupplierId`) pour qu'App puisse mémoriser vers où
    // ramener l'utilisateur depuis SupplierDetailView. Sans ce wrapper,
    // App n'a pas connaissance du supplier dont on consulte le profil.
    const handleOpenCampaign = onNavigateToCampaign
      ? (campaignId: string, options?: { campaignSupplierId?: string }) => {
          onNavigateToCampaign(campaignId, {
            campaignSupplierId: options?.campaignSupplierId,
            originSupplierId: activeSupplierId,
          })
        }
      : undefined
    return (
      <div ref={containerRef}>
        <SupplierProfile
          supplierId={activeSupplierId}
          onBack={handleCloseProfile}
          onOpenCampaign={handleOpenCampaign}
        />
      </div>
    )
  }

  return (
    <div ref={containerRef} className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Fournisseurs</h1>
          <p className="text-muted-foreground">
            Suivez l'engagement de vos fournisseurs cross-campagne
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)} className="shrink-0">
          <Plus className="h-4 w-4 mr-1.5" />
          Ajouter un fournisseur
        </Button>
      </div>

      <SupplierSearchInput
        initialValue={initial.search}
        onDebouncedChange={handleSearch}
        registerFocusRef={(el) => {
          searchInputRef.current = el
        }}
      />

      <SupplierFilters
        filter={filter}
        counters={data?.counters ?? null}
        onFilterChange={handleFilterChange}
      />

      {showInitialSkeleton ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : data && data.total === 0 && filter === 'all' && search === '' ? (
        <EmptyState
          icon="👥"
          title="Aucun fournisseur"
          description="Ajoutez votre premier fournisseur ou demandez à l'agent de les importer"
          primaryLabel="Demander à l'agent"
          secondaryLabel="+ Ajouter un fournisseur"
          onSecondaryClick={() => setCreateOpen(true)}
        />
      ) : data && data.data.length === 0 ? (
        <div className="rounded-lg border bg-muted/20 p-8 text-center text-sm text-muted-foreground">
          Aucun fournisseur correspondant à votre recherche.
        </div>
      ) : data ? (
        <>
          <BulkActions
            selectedIds={Array.from(selectedIds)}
            isExporting={isExporting}
            onExport={handleExport}
            onClear={handleClearSelection}
            onRelaunch={handleOpenRemind}
          />
          <SuppliersTable
            suppliers={data.data}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={handleSort}
            reloading={isReloading}
            onRowClick={handleOpenProfile}
            selectedIds={selectedIds}
            onToggleSelect={handleToggleSelect}
            onToggleSelectAll={handleToggleSelectAll}
          />
          {totalPages > 1 && (
            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <span>
                Page {data.page} sur {totalPages} — {data.total} fournisseur
                {data.total > 1 ? 's' : ''}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={data.page <= 1 || isReloading}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Précédent
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={data.page >= totalPages || isReloading}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Suivant
                </Button>
              </div>
            </div>
          )}
        </>
      ) : loadError ? (
        <div
          role="alert"
          className="rounded-lg border border-destructive/30 bg-destructive/5 p-8 text-center"
        >
          <p className="text-sm text-destructive">{loadError}</p>
          <Button
            variant="outline"
            size="sm"
            className="mt-3"
            onClick={() => loadData()}
            disabled={isReloading}
          >
            Réessayer
          </Button>
        </div>
      ) : null}

      <CreateSupplierDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={loadData}
      />

      {remindOpen && (
        <BulkRemindModal
          selectedSupplierIds={Array.from(selectedIds)}
          selectedSupplierNames={selectedSupplierNames}
          memberName={user?.name ?? user?.email ?? 'Équipe'}
          memberEmail={user?.email ?? ''}
          open={remindOpen}
          onOpenChange={setRemindOpen}
          onSuccess={handleRemindSuccess}
        />
      )}
    </div>
  )
}
